import React, { useState, useEffect } from 'react';
import { 
  Search, Video, Brain, Target, CheckCircle, XCircle, 
  Sparkles, Award, Heart, Globe, Shield, Eye, 
  Lightbulb, TrendingUp, Clock, AlertCircle, Play,
  ThumbsUp, ThumbsDown, ExternalLink, Filter,
  RefreshCw, BarChart3, Users, Settings, Plus, Server,
  MapPin, Languages, X, Loader2
} from 'lucide-react';

import videoServices from '../CP-Academy/VideoService';
import sourceService from '../CP-Sourcing/SourceService';
import { useAdminAuth } from './AdminAuthContext';

// Configuration des thèmes vidéo Aladinn
const VIDEO_THEMES = {
  entrepreneurship: { 
    name: '🚀 Entrepreneuriat', 
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    keywords: ['entrepreneuriat', 'startup', 'business', 'innovation', 'leadership']
  },
  technology: { 
    name: '💻 Technologie', 
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    keywords: ['technologie', 'digital', 'IA', 'développement', 'innovation']
  },
  finance: { 
    name: '💰 Finance', 
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    keywords: ['finance', 'investissement', 'économie', 'gestion', 'comptabilité']
  },
  leadership: { 
    name: '👑 Leadership', 
    color: 'orange',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    keywords: ['leadership', 'management', 'équipe', 'motivation', 'coaching']
  },
  health: { 
    name: '🌱 Santé', 
    color: 'teal',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    keywords: ['santé', 'bien-être', 'fitness', 'nutrition', 'mindfulness']
  },
  creativity: { 
    name: '🎨 Créativité', 
    color: 'pink',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    keywords: ['créativité', 'art', 'design', 'innovation', 'imagination']
  }
};

// Providers vidéo disponibles
const VIDEO_PROVIDERS = [
  { id: 'VideoEducation', name: 'VideoEducation', description: 'Vidéos éducatives ou professionnelles' },
  { id: 'YouTube', name: 'YouTube', description: 'Vidéos YouTuube' }
];

// Fonction utilitaire pour récupérer l'userId du token
const getUserIdFromToken = (token) => {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.uid || payload.user_id;
  } catch (error) {
    console.error('Erreur décodage token pour userId:', error);
    return null;
  }
};

// Composant de formulaire de recherche vidéo
const VideoSearchForm = ({ onSearch, loading, selectedTheme, onThemeChange }) => {
  const [query, setQuery] = useState('');
  const [selectedProviders, setSelectedProviders] = useState(['VideoEducation', 'YouTube']);
  const [maxSources, setMaxSources] = useState(20);
  const [languages, setLanguages] = useState(['fr', 'en']);
  const [country, setCountry] = useState('FR');
  const [useAI, setUseAI] = useState(true);

  const handleProviderToggle = (providerId) => {
    setSelectedProviders(prev => 
      prev.includes(providerId) 
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  const handleLanguageToggle = (lang) => {
    setLanguages(prev => 
      prev.includes(lang) 
        ? prev.filter(l => l !== lang)
        : [...prev, lang]
    );
  };

  const handleSearch = () => {
    const themeData = VIDEO_THEMES[selectedTheme];
    const searchParams = {
      userObjective: `Vidéo de qualité et populaire sur le thème ${themeData.name} : ${themeData.keywords.join(', ')}`,
      query: query.trim() || ` ${themeData.keywords[0]} `,
      maxSources,
      selectedProviders,
      languages,
      country,
      theme: selectedTheme,
      contentType: 'video',
      useAI
    };
    
    onSearch(searchParams);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
        <Search className="w-6 h-6 text-blue-600" />
        Découverte Intelligente de Formations Vidéo
      </h3>

      {/* Sélection du thème */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Thème de formation
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(VIDEO_THEMES).map(([key, theme]) => (
            <button
              key={key}
              onClick={() => onThemeChange(key)}
              className={`p-4 rounded-xl border transition-all ${
                selectedTheme === key
                  ? `${theme.bgColor} ${theme.borderColor} text-${theme.color}-800`
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">{theme.name}</div>
              <div className="text-xs text-gray-500 mt-1">
                {theme.keywords.slice(0, 2).join(', ')}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Requête personnalisée */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Requête spécifique (optionnel)
        </label>
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Affiner : "${VIDEO_THEMES[selectedTheme]?.keywords[0]} avancé", "méthodes ${VIDEO_THEMES[selectedTheme]?.keywords[0]}"...`}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Si vide, une requête optimisée sera générée automatiquement
        </p>
      </div>

      {/* Options avancées dans un layout compact */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Providers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Sources de données
          </label>
          <div className="space-y-2">
            {VIDEO_PROVIDERS.map(provider => (
              <label
                key={provider.id}
                className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedProviders.includes(provider.id)
                    ? 'bg-purple-50 border-purple-300'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedProviders.includes(provider.id)}
                  onChange={() => handleProviderToggle(provider.id)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <div className="ml-3">
                  <div className="font-medium text-gray-900 text-sm">{provider.name}</div>
                  <div className="text-xs text-gray-500">{provider.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Paramètres */}
        <div className="space-y-4">
          {/* Langues */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Langues
            </label>
            <div className="flex gap-2">
              {['fr', 'en'].map(lang => (
                <button
                  key={lang}
                  onClick={() => handleLanguageToggle(lang)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                    languages.includes(lang)
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Languages className="w-4 h-4 inline mr-1" />
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Pays */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Région ciblée
            </label>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            >
              <option value="FR">🇫🇷 France</option>
              <option value="MA">🇲🇦 Maroc</option>
              <option value="CA">🇨🇦 Canada</option>
              <option value="US">🇺🇸 États-Unis</option>
            </select>
          </div>

          {/* Nombre de sources */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de sources
            </label>
            <select
              value={maxSources}
              onChange={(e) => setMaxSources(Number(e.target.value))}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            >
              <option value={10}>10 sources</option>
              <option value={20}>20 sources</option>
              <option value={30}>30 sources</option>
            </select>
          </div>

          {/* IA */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={useAI}
                onChange={(e) => setUseAI(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="ml-2 text-sm text-gray-700">Analyse IA avancée</span>
            </label>
          </div>
        </div>
      </div>

      {/* Bouton de recherche */}
      <div className="mt-6 text-center">
        <button
          onClick={handleSearch}
          disabled={loading || selectedProviders.length === 0}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2 mx-auto"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Découverte en cours...
            </>
          ) : (
            <>
              <Search className="w-5 h-5" />
              Découvrir des formations
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Composant d'analyse vidéo avec critères Aladinn
const VideoAnalysisCard = ({ source, onApprove, onReject, onPreview }) => {
  // Analyser les critères Aladinn basés sur les données réelles
  const analyzeAladinnCriteria = (source) => {
    const analysis = {
      pedagogical: Math.min(10, Math.round((source.relevanceScore || 5) * 2)),
      ethical: Math.min(10, Math.round(8 + (source.reliability === 'High' ? 2 : source.reliability === 'Medium' ? 0 : -2))),
      inspirational: Math.min(10, Math.round((source.popularityScore || 5) * 1.5)),
      responsible: Math.min(10, Math.round(7 + (source.type === 'Educational' ? 3 : source.type === 'Governmental' ? 2 : 1))),
      popular: Math.min(10, Math.round((source.popularityScore || 5) * 1.8))
    };

    const averageScore = Object.values(analysis).reduce((sum, score) => sum + score, 0) / 5;
    
    let recommendation = 'Non recommandé';
    if (averageScore >= 8.5) recommendation = 'Très recommandé';
    else if (averageScore >= 7) recommendation = 'Recommandé';
    else if (averageScore >= 5.5) recommendation = 'À vérifier';

    const reasons = [];
    if (analysis.pedagogical >= 8) reasons.push('Excellent potentiel pédagogique');
    if (analysis.ethical >= 8) reasons.push('Aligné avec les valeurs éthiques');
    if (source.type === 'Educational') reasons.push('Source éducative officielle');
    if (source.reliability === 'High') reasons.push('Fiabilité élevée confirmée');

    return {
      criteria: analysis,
      recommendation,
      reasons,
      averageScore: Math.round(averageScore * 10) / 10,
      summary: `Source ${recommendation.toLowerCase()} avec un score global de ${averageScore.toFixed(1)}/10`
    };
  };

  const analysis = analyzeAladinnCriteria(source);

  // États de la source
  const isApproved = source.status === 'approved';
  const isRejected = source.status === 'rejected';
  const isPending = source.status === 'pending' || !source.status;

  const getCriteriaColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getRecommendationStyle = (recommendation) => {
    switch (recommendation) {
      case 'Très recommandé':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Recommandé':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'À vérifier':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case 'Très recommandé':
      case 'Recommandé':
        return <CheckCircle className="w-4 h-4" />;
      case 'À vérifier':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <XCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-all ${
      isApproved ? 'border-green-200 bg-green-50/50' :
      isRejected ? 'border-red-200 bg-red-50/50' :
      'border-gray-100'
    }`}>
      {/* Header avec statut */}
      <div className={`relative h-32 ${
        isApproved ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
        isRejected ? 'bg-gradient-to-br from-red-500 to-rose-600' :
        'bg-gradient-to-br from-blue-500 to-purple-600'
      }`}>
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            source.type === 'Educational' ? 'bg-green-500' :
            source.type === 'Governmental' ? 'bg-blue-500' :
            'bg-purple-500'
          } text-white`}>
            {source.type}
          </span>
        </div>
        <div className="absolute top-4 right-4">
          {isApproved && (
            <div className="px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-200 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              <span className="font-medium text-sm">✅ Approuvée</span>
            </div>
          )}
          {isRejected && (
            <div className="px-3 py-1 rounded-full bg-red-100 text-red-800 border border-red-200 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              <span className="font-medium text-sm">❌ Rejetée</span>
            </div>
          )}
          {isPending && (
            <div className={`px-3 py-1 rounded-full border flex items-center gap-2 ${getRecommendationStyle(analysis.recommendation)}`}>
              {getRecommendationIcon(analysis.recommendation)}
              <span className="font-medium text-sm">{analysis.recommendation}</span>
            </div>
          )}
        </div>
        <div className="absolute bottom-4 left-4 text-white">
          <div className="text-lg font-bold">{analysis.averageScore}/10</div>
          <div className="text-xs opacity-90">Score global</div>
        </div>
      </div>

      <div className="p-6">
        {/* Titre et description */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {source.title}
        </h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {source.description}
        </p>

        {/* Métadonnées de la source */}
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
          <span className="flex items-center gap-1">
            <Globe className="w-4 h-4" />
            {source.domain}
          </span>
          <span className="flex items-center gap-1">
            <Shield className="w-4 h-4" />
            {source.reliability}
          </span>
          <span className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            Score: {source.relevanceScore}/10
          </span>
        </div>

        {/* Analyse IA Aladinn */}
        <div className="mb-6">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Brain className="w-4 h-4 text-purple-600" />
            Analyse Critères Aladinn
          </h4>
          
          {/* Résumé IA */}
          <div className={`p-3 rounded-lg mb-4 ${
            isApproved ? 'bg-green-50 text-green-800' :
            isRejected ? 'bg-red-50 text-red-800' :
            'bg-blue-50 text-blue-800'
          }`}>
            <p className="text-sm">{analysis.summary}</p>
          </div>

          {/* Critères de scoring */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {Object.entries(analysis.criteria).map(([key, score]) => {
              const labels = {
                pedagogical: 'Pédagogique',
                inspirational: 'Inspirant',
                ethical: 'Éthique',
                popular: 'Populaire',
                responsible: 'Responsable'
              };
              
              return (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{labels[key]}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCriteriaColor(score)}`}>
                    {score}/10
                  </span>
                </div>
              );
            })}
          </div>

          {/* Raisons de la recommandation */}
          {analysis.reasons.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Points forts :</p>
              <ul className="space-y-1">
                {analysis.reasons.map((reason, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => onPreview(source)}
            className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <Eye className="w-4 h-4" />
            Prévisualiser
          </button>
          
          {isPending && (
            <>
              <button
                onClick={() => onApprove(source)}
                className="bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center"
                title="Approuver et publier"
              >
                <ThumbsUp className="w-4 h-4" />
              </button>
              
              <button
                onClick={() => onReject(source)}
                className="bg-red-600 text-white px-4 py-3 rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center"
                title="Rejeter"
              >
                <ThumbsDown className="w-4 h-4" />
              </button>
            </>
          )}
          
          {isApproved && (
            <div className="bg-green-100 text-green-800 px-4 py-3 rounded-xl flex items-center justify-center">
              <span className="text-sm font-medium">Publiée ✅</span>
            </div>
          )}
          
          {isRejected && (
            <div className="bg-red-100 text-red-800 px-4 py-3 rounded-xl flex items-center justify-center">
              <span className="text-sm font-medium">Rejetée ❌</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant de filtres par statut
const StatusFilters = ({ statusFilter, onStatusChange, stats }) => {
  const filters = [
    { id: 'all', label: 'Toutes', count: stats.discovered, color: 'bg-gray-100 text-gray-800' },
    { id: 'pending', label: 'En attente', count: stats.pending, color: 'bg-blue-100 text-blue-800' },
    { id: 'approved', label: 'Approuvées', count: stats.approved, color: 'bg-green-100 text-green-800' },
    { id: 'rejected', label: 'Rejetées', count: stats.rejected, color: 'bg-red-100 text-red-800' }
  ];

  return (
    <div className="flex gap-2 mb-6">
      {filters.map(filter => (
        <button
          key={filter.id}
          onClick={() => onStatusChange(filter.id)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
            statusFilter === filter.id
              ? filter.color + ' shadow-sm'
              : 'bg-white border border-gray-200 hover:bg-gray-50'
          }`}
        >
          {filter.label}
          <span className={`px-2 py-0.5 rounded-full text-xs ${
            statusFilter === filter.id ? 'bg-white/50' : 'bg-gray-100'
          }`}>
            {filter.count}
          </span>
        </button>
      ))}
    </div>
  );
};

// Composant principal de découverte vidéo administrateur
const AdminVideoDiscovery = () => {
  const { isAuthenticated, user } = useAdminAuth();
  const [selectedTheme, setSelectedTheme] = useState('entrepreneurship');
  const [discoveredSources, setDiscoveredSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('discover');
  const [approvedSources, setApprovedSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [backendStatus, setBackendStatus] = useState('unknown');
  const [discoveryProgress, setDiscoveryProgress] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Vérifier le statut backend au chargement
  useEffect(() => {
    checkBackendStatus();
  }, []);

  const checkBackendStatus = async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        setBackendStatus('no_auth');
        return;
      }

      const providers = await sourceService.getProvidersMetadata(token);
      setBackendStatus('connected');
      console.log('✅ Backend connecté pour découverte vidéo');
    } catch (error) {
      console.error('❌ Backend déconnecté:', error);
      setBackendStatus('error');
    }
  };

  const getAuthToken = async () => {
    if (!user || !isAuthenticated) {
      console.error('❌ Utilisateur non connecté');
      return null;
    }
    
    try {
      const token = await user.getIdToken();
      return token;
    } catch (error) {
      console.error('❌ Erreur token:', error);
      return null;
    }
  };

  // Fonction principale de découverte via backend réel
  const handleVideoSearch = async (searchParams) => {
    const token = await getAuthToken();
    if (!token) {
      alert('Connectez-vous d\'abord');
      return;
    }

    const realUserId = getUserIdFromToken(token);
    if (!realUserId) {
      alert('Impossible de récupérer l\'ID utilisateur du token');
      return;
    }

    setLoading(true);
    setDiscoveryProgress('🔍 Recherche de formations en cours...');
    
    try {
      // Utiliser le vrai SourceService existant
      const result = await sourceService.getRecommendedSources({
        userId: realUserId,
        userObjective: searchParams.userObjective,
        query: searchParams.query,
        maxSources: searchParams.maxSources,
        selectedProviders: searchParams.selectedProviders,
        languages: searchParams.languages,
        country: searchParams.country,
        useAI: searchParams.useAI,
        // Paramètres spécifiques pour les vidéos de formation
        preferredTypes: ['Educational', 'Media'],
        contentFocus: 'training',
        theme: searchParams.theme
      }, token);

      if (result.sources && result.sources.length > 0) {
        // Filtrer et enrichir les sources pour les formations vidéo
        const videoSources = result.sources.filter(source => 
          source.description?.toLowerCase().includes('video') ||
          source.description?.toLowerCase().includes('formation') ||
          source.description?.toLowerCase().includes('cours') ||
          source.description?.toLowerCase().includes('training') ||
          source.title?.toLowerCase().includes('video') ||
          source.url?.includes('youtube') ||
          source.url?.includes('vimeo') ||
          source.type === 'Educational'
        ).map((source, index) => ({
          ...source,
          // Créer un ID unique et stable
          id: source.id || `source-${Date.now()}-${index}`,
          status: 'pending' // ✅ État initial
        }));

        setDiscoveredSources(videoSources);
        setDiscoveryProgress(`✅ ${videoSources.length} formations vidéo trouvées !`);
        
        console.log('Sources vidéo découvertes:', videoSources);
      } else {
        setDiscoveredSources([]);
        setDiscoveryProgress('❌ Aucune formation trouvée pour ces critères');
      }
      
      setTimeout(() => setDiscoveryProgress(''), 3000);
      
    } catch (error) {
      console.error('Erreur lors de la découverte:', error);
      setDiscoveryProgress(`❌ Erreur: ${error.message}`);
      setTimeout(() => setDiscoveryProgress(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fonction d'approbation corrigée
  const handleApproveSource = async (source) => {
    try {
      // Enrichir les données de la source pour la base vidéo
      const videoData = {
        title: source.title,
        description: source.description,
        url: source.usableUrl || source.url,
        theme: selectedTheme,
        source: 'backend_discovery',
        discoveryMethod: 'backend_ai',
        type: source.type,
        reliability: source.reliability,
        relevanceScore: source.relevanceScore,
        popularityScore: source.popularityScore,
        domain: source.domain,
        addedBy: user?.email,
        addedAt: new Date().toISOString(),
        status: 'active',
        aiValidated: true
      };

      // Ajouter à la base de données
      await videoServices.addVideoToDatabase(videoData, user?.email, selectedTheme);
      
      // ✅ CHANGEMENT : Marquer comme approuvée au lieu de supprimer
      setDiscoveredSources(prev => 
        prev.map(s => 
          s.id === source.id 
            ? { ...s, status: 'approved' }
            : s
        )
      );
      
      // Ajouter aux sources approuvées
      setApprovedSources(prev => [...prev, { ...source, ...videoData }]);
      
      // Feedback utilisateur
      setDiscoveryProgress(`✅ Formation "${source.title}" approuvée et publiée !`);
      setTimeout(() => setDiscoveryProgress(''), 3000);
      
      console.log('✅ Formation approuvée et publiée:', source.title);
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'approbation:', error);
      setDiscoveryProgress(`❌ Erreur: ${error.message}`);
      setTimeout(() => setDiscoveryProgress(''), 5000);
    }
  };

  // ✅ Fonction de rejet corrigée
  const handleRejectSource = (source) => {
    // ✅ CHANGEMENT : Marquer comme rejetée au lieu de supprimer
    setDiscoveredSources(prev => 
      prev.map(s => 
        s.id === source.id 
          ? { ...s, status: 'rejected' }
          : s
      )
    );
    
    setDiscoveryProgress(`❌ Formation "${source.title}" rejetée`);
    setTimeout(() => setDiscoveryProgress(''), 2000);
    
    console.log('❌ Formation rejetée:', source.title);
  };

  const handlePreviewSource = (source) => {
    setSelectedSource(source);
    setShowPreview(true);
  };

  // Filtrer les sources selon le statut
  const filteredSources = discoveredSources.filter(source => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return source.status === 'pending' || !source.status;
    return source.status === statusFilter;
  });

  // ✅ Statistiques mises à jour
  const stats = {
    discovered: discoveredSources.length,
    approved: discoveredSources.filter(s => s.status === 'approved').length,
    rejected: discoveredSources.filter(s => s.status === 'rejected').length,
    pending: discoveredSources.filter(s => s.status === 'pending' || !s.status).length,
    averageScore: discoveredSources.length > 0 
      ? Math.round(discoveredSources.reduce((acc, s) => acc + (s.relevanceScore || 5), 0) / discoveredSources.length * 10) / 10
      : 0,
    highQuality: discoveredSources.filter(s => (s.relevanceScore || 5) >= 8).length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header avec statut backend */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                🎓 Découverte IA de Formations Vidéo
              </h1>
              <p className="text-gray-600">
                Système intelligent de découverte, analyse et validation de formations alignées Aladinn
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-xl border flex items-center gap-2 ${
                backendStatus === 'connected' 
                  ? 'bg-green-50 border-green-200 text-green-800' 
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <Server className="w-5 h-5" />
                <span className="font-medium">
                  {backendStatus === 'connected' ? 'Backend IA connecté' : 'Backend déconnecté'}
                </span>
              </div>
              
              <button onClick={checkBackendStatus} className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Statistiques en temps réel */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-xl">
                <Search className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.discovered}</div>
                <div className="text-sm text-gray-600">Total découvertes</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-100 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.pending}</div>
                <div className="text-sm text-gray-600">En attente</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-3 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.approved}</div>
                <div className="text-sm text-gray-600">Approuvées</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-red-100 p-3 rounded-xl">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.rejected}</div>
                <div className="text-sm text-gray-600">Rejetées</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-3 rounded-xl">
                <Brain className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.averageScore}</div>
                <div className="text-sm text-gray-600">Score IA moyen</div>
              </div>
            </div>
          </div>
        </div>

        {/* Progression de découverte */}
        {discoveryProgress && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-blue-800">
              <Brain className="w-5 h-5" />
              <span className="font-medium">{discoveryProgress}</span>
            </div>
          </div>
        )}

        {/* Navigation par onglets */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-8">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('discover')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'discover'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Search className="w-5 h-5 inline-block mr-2" />
              Découverte & Curation ({stats.discovered})
            </button>
            
            <button
              onClick={() => setActiveTab('approved')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'approved'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <CheckCircle className="w-5 h-5 inline-block mr-2" />
              Historique Publiées ({stats.approved})
            </button>
          </div>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'discover' && (
          <div>
            {/* Formulaire de recherche intégré */}
            <VideoSearchForm
              onSearch={handleVideoSearch}
              loading={loading}
              selectedTheme={selectedTheme}
              onThemeChange={setSelectedTheme}
            />

            {/* Filtres par statut */}
            {discoveredSources.length > 0 && (
              <StatusFilters
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                stats={stats}
              />
            )}

            {/* Résultats de découverte avec vraies données */}
            {filteredSources.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  Formations pour {VIDEO_THEMES[selectedTheme]?.name} 
                  {statusFilter !== 'all' && (
                    <span className="text-gray-500 font-normal"> - {
                      statusFilter === 'pending' ? 'En attente' :
                      statusFilter === 'approved' ? 'Approuvées' :
                      statusFilter === 'rejected' ? 'Rejetées' : ''
                    }</span>
                  )}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredSources.map(source => (
                    <VideoAnalysisCard
                      key={source.id}
                      source={source}
                      onApprove={handleApproveSource}
                      onReject={handleRejectSource}
                      onPreview={handlePreviewSource}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Message si aucune découverte */}
            {!loading && discoveredSources.length === 0 && (
              <div className="text-center py-12">
                <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Aucune formation découverte
                </h3>
                <p className="text-gray-600 mb-6">
                  Utilisez le formulaire ci-dessus pour découvrir des formations via le backend IA
                </p>
                {backendStatus !== 'connected' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-md mx-auto">
                    <AlertCircle className="w-5 h-5 text-yellow-600 inline mr-2" />
                    <span className="text-yellow-800">Backend déconnecté - Vérifiez la connexion</span>
                  </div>
                )}
              </div>
            )}

            {/* Message si aucune source après filtrage */}
            {!loading && discoveredSources.length > 0 && filteredSources.length === 0 && (
              <div className="text-center py-12">
                <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Aucune formation {statusFilter === 'pending' ? 'en attente' : statusFilter === 'approved' ? 'approuvée' : 'rejetée'}
                </h3>
                <p className="text-gray-600">
                  Changez le filtre pour voir d'autres formations découvertes
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'approved' && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Formations approuvées et publiées dans l'Academy
            </h2>
            
            {approvedSources.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {approvedSources.map(source => (
                  <div key={source.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{source.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{source.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          ✅ Publié
                        </span>
                        <span className="text-xs text-gray-500">
                          {source.theme}
                        </span>
                      </div>
                      <button
                        onClick={() => handlePreviewSource(source)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Aucune formation approuvée
                </h3>
                <p className="text-gray-600">
                  Les formations que vous approuvez apparaîtront ici et seront publiées dans l'Academy
                </p>
              </div>
            )}
          </div>
        )}

        {/* Modal de prévisualisation */}
        {showPreview && selectedSource && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-gray-900">Prévisualisation de la formation</h3>
                  <button 
                    onClick={() => setShowPreview(false)}
                    className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {selectedSource.title}
                </h4>
                <p className="text-gray-600 mb-4">{selectedSource.description}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <span className="text-sm text-gray-500">Type:</span>
                    <p className="font-medium">{selectedSource.type}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Fiabilité:</span>
                    <p className="font-medium">{selectedSource.reliability}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Score de pertinence:</span>
                    <p className="font-medium">{selectedSource.relevanceScore}/10</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Domaine:</span>
                    <p className="font-medium">{selectedSource.domain}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Statut:</span>
                    <p className={`font-medium ${
                      selectedSource.status === 'approved' ? 'text-green-600' :
                      selectedSource.status === 'rejected' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {selectedSource.status === 'approved' ? '✅ Approuvée' :
                       selectedSource.status === 'rejected' ? '❌ Rejetée' :
                       '⏳ En attente'}
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-100 rounded-lg p-4 mb-6 text-center">
                  <a
                    href={selectedSource.usableUrl || selectedSource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Ouvrir la formation
                  </a>
                </div>
                
                {selectedSource.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        handleApproveSource(selectedSource);
                        setShowPreview(false);
                      }}
                      className="flex-1 bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700"
                    >
                      ✅ Approuver et publier dans l'Academy
                    </button>
                    <button
                      onClick={() => {
                        handleRejectSource(selectedSource);
                        setShowPreview(false);
                      }}
                      className="bg-red-600 text-white px-4 py-3 rounded-xl hover:bg-red-700"
                    >
                      ❌ Rejeter
                    </button>
                  </div>
                )}
                
                {selectedSource.status !== 'pending' && (
                  <div className="text-center">
                    <button
                      onClick={() => setShowPreview(false)}
                      className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300"
                    >
                      Fermer
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVideoDiscovery;